/**
 * Month/window arithmetic for a plan's date range — extracted from `PlanSetupDialog.tsx`
 * (the "Edit setup" dialog) so it is unit-testable without a component harness.
 *
 * See that file's header for the product context: narrowing a Draft's date range now
 * PERMANENTLY DISCARDS hours already entered past the new end date (server-side deletion +
 * audit lives elsewhere). This module computes what a save is about to discard, client-side,
 * so the dialog can warn honestly before the planner confirms.
 */

import { parseHoursCenti } from "./resource-plan-math";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Inclusive month count between two `YYYY-MM` strings, or 0 if either is malformed. */
export function monthSpan(start: string, end: string): number {
  if (!MONTH.test(start) || !MONTH.test(end)) return 0;
  const [sy, sm] = start.split("-").map(Number);
  const [ey, em] = end.split("-").map(Number);
  if (sy === undefined || sm === undefined || ey === undefined || em === undefined) return 0;
  return Math.max(0, (ey - sy) * 12 + (em - sm) + 1);
}

/** `count` consecutive months starting at `from`, `YYYY-MM`. */
export function monthRange(from: string, count: number): string[] {
  if (!MONTH.test(from) || count <= 0) return [];
  const [y, m] = from.split("-").map(Number);
  if (y === undefined || m === undefined) return [];
  return Array.from({ length: count }, (_, i) => {
    const ordinal = y * 12 + (m - 1) + i;
    return `${Math.floor(ordinal / 12)}-${String((ordinal % 12) + 1).padStart(2, "0")}`;
  });
}

/** Months between two months (`YYYY-MM` or `YYYY-MM-01`). Negative when `to` is earlier. */
export function monthOffset(from: string, to: string): number {
  const [fy, fm] = [Number(from.slice(0, 4)), Number(from.slice(5, 7))];
  const [ty, tm] = [Number(to.slice(0, 4)), Number(to.slice(5, 7))];
  return (ty - fy) * 12 + (tm - fm);
}

/** Shift a `YYYY-MM` by a whole number of months. Returns `YYYY-MM`. */
export function shiftMonth(month: string, offset: number): string {
  const zero = Number(month.slice(0, 4)) * 12 + (Number(month.slice(5, 7)) - 1) + offset;
  return `${Math.floor(zero / 12)}-${String((zero % 12) + 1).padStart(2, "0")}`;
}

/** The fields `prospectiveWindowMonths` reads off a plan — narrowed from `ResourcePlanDetailView`. */
export interface PlanWindowSource {
  endMonth: string | null;
  months: string[];
}

/**
 * The months the form's prospective window covers — or `null` when that is unknowable, in
 * which case no out-of-window claim can honestly be made.
 *
 * With no end month the window runs to the Planning Horizon, a constant the client does not
 * have. If the plan ALREADY has no end month, the current column count IS that horizon and
 * stands in for it. But if the plan is bounded and the planner clears the end month, the
 * window WIDENS to that unknown horizon — using the old bounded length would warn that
 * months fall outside a window which now contains them, so say nothing instead.
 */
export function prospectiveWindowMonths(
  startMonth: string,
  endMonth: string,
  plan: PlanWindowSource,
): Set<string> | null {
  if (endMonth !== "" && MONTH.test(endMonth)) {
    return new Set(monthRange(startMonth, monthSpan(startMonth, endMonth)));
  }
  if (plan.endMonth !== null) return null;
  return new Set(monthRange(startMonth, Math.max(1, plan.months.length)));
}

/** How many month codes a warning spells out before it summarises the rest. */
export const MONTH_LIST_LIMIT = 6;

/** Month codes as a readable list — a 24-month narrowing must not fill a paragraph. */
export function monthList(months: readonly string[]): string {
  if (months.length <= MONTH_LIST_LIMIT) return months.join(", ");
  return `${months.slice(0, MONTH_LIST_LIMIT).join(", ")} +${months.length - MONTH_LIST_LIMIT} more`;
}

/** The fields `outOfWindowMonths` reads off a plan line — narrowed from `PlanLineView`. */
export interface PlanLineWindowSource {
  months: { month: string; hours: string }[];
}

/** Distinct months, across every line, that carry non-zero hours outside `window`. */
export function outOfWindowMonths(
  lines: readonly PlanLineWindowSource[],
  window: ReadonlySet<string>,
): string[] {
  const months = new Set<string>();
  for (const line of lines) {
    for (const cell of line.months) {
      if (window.has(cell.month)) continue;
      if (Number(cell.hours) > 0) months.add(cell.month);
    }
  }
  return [...months].sort();
}

/**
 * Total non-zero hours, across every line, in cells outside `window` — the figure a
 * narrowing save is about to discard. Decimal string ("1280.00"), never a float: summed in
 * centi-hours via `resource-plan-math.ts`'s `parseHoursCenti`, the same integer scale the
 * server totals hours at, then rendered back to a plain decimal for `formatPlanHours`.
 *
 * Same non-zero gate as `outOfWindowMonths` (`Number(cell.hours) > 0`), so the two always
 * agree on which cells count — a warning that lists N months must total the same N months'
 * hours, not a different set filtered by a different rule.
 */
export function outOfWindowHours(
  lines: readonly PlanLineWindowSource[],
  window: ReadonlySet<string>,
): string {
  let totalCenti = 0;
  for (const line of lines) {
    for (const cell of line.months) {
      if (window.has(cell.month)) continue;
      if (Number(cell.hours) <= 0) continue;
      totalCenti += parseHoursCenti(cell.hours);
    }
  }
  const whole = Math.trunc(totalCenti / 100);
  const frac = totalCenti % 100;
  return `${whole}.${String(frac).padStart(2, "0")}`;
}

/** The fields `unevenRunRateLines` reads off a plan line — narrowed from `PlanLineView`. */
export interface PlanLineRunRateSource extends PlanLineWindowSource {
  laborCategoryName: string;
  shares?: PlanLineWindowSource[];
}

/** True when `cells` carry the same hours in every month of `window` (an empty month is 0). */
function isLevel(cells: PlanLineWindowSource["months"], window: readonly string[]): boolean {
  const byMonth = new Map(cells.map((c) => [c.month, parseHoursCenti(c.hours)]));
  return new Set(window.map((m) => byMonth.get(m) ?? 0)).size <= 1;
}

/**
 * The lines that cannot become one hours-per-month figure (RP-4.4 Steady-State) — their
 * hours, or any of their Shares' hours, differ between the months of `window`.
 *
 * A month with no hours counts as 0, so a line filled in for only part of the window is
 * uneven. That is stricter than the server's collapse, which only compares the cells that
 * exist and would otherwise spread three months of hours across all twelve.
 */
export function unevenRunRateLines(
  lines: readonly PlanLineRunRateSource[],
  window: readonly string[],
): string[] {
  return lines
    .filter(
      (l) => !isLevel(l.months, window) || (l.shares ?? []).some((s) => !isLevel(s.months, window)),
    )
    .map((l) => l.laborCategoryName);
}
